import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import Card from '../components/ui/Card';

const bold = { strong: <strong /> };

export default function TermsOfUse() {
  const navigate = useNavigate();
  const location = useLocation();
  // The Terms are published in English only, whatever the UI language.
  const { t } = useTranslation('terms', { lng: 'en' });

  const handleBack = () => {
    // Check if user came from within the app
    const fromPath = location.state?.from;
    if (fromPath) {
      navigate(fromPath);
    } else {
      // Default to home page
      navigate('/');
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container-custom py-12">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-primary hover:text-primary-dark mb-6 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            {t('back')}
          </button>

          {/* Content */}
          <Card padding="lg" className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-900 mb-4 text-center">{t('title')}</h1>
            <p className="text-sm text-gray-500 text-center mb-2">{t('effectiveDate')}</p>
            <p className="text-sm text-gray-500 text-center mb-8">{t('entity')}</p>

            <p className="text-gray-700 mb-4">{t('intro')}</p>
            <p className="text-gray-700 mb-8 font-medium">{t('agreementNote')}</p>

            {/* Section 1: Overview */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.overview.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.overview.content')}</p>
              <p className="text-gray-700">{t('sections.overview.relationship')}</p>
            </section>

            {/* Section 2: Eligibility */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.eligibility.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.eligibility.intro')}</p>
              <div className="space-y-2 text-gray-700 mb-3">
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.eligibility.items.age')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.eligibility.items.accuracy')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.eligibility.items.compliance')}</p>
                </div>
              </div>
              <p className="text-gray-700">{t('sections.eligibility.guardianNote')}</p>
            </section>

            {/* Section 3: Platform Role */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.platformRole.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.platformRole.intro')}</p>
              <div className="space-y-2 text-gray-700 mb-3">
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.platformRole.items.contract')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.platformRole.items.role')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.platformRole.items.responsibility')}</p>
                </div>
              </div>
              <p className="text-gray-700">{t('sections.platformRole.hostNote')}</p>
            </section>

            {/* Section 4: Accounts */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.accounts.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.accounts.intro')}</p>
              <div className="space-y-2 text-gray-700 mb-3">
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.accounts.items.accurate')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.accounts.items.confidential')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.accounts.items.responsibility')}</p>
                </div>
              </div>
              <p className="text-gray-700">{t('sections.accounts.reserveRight')}</p>
            </section>

            {/* Section 5: Payments */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.payments.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.payments.intro')}</p>
              <div className="space-y-2 text-gray-700 mb-3">
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.payments.items.hostPolicies')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.payments.items.serviceFees')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.payments.items.priceChanges')}</p>
                </div>
              </div>
              <p className="text-gray-700">{t('sections.payments.cancelNote')}</p>
            </section>

            {/* Section 6: Cancellations */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.cancellations.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.cancellations.content')}</p>
              <p className="text-gray-700">{t('sections.cancellations.assistNote')}</p>
            </section>

            {/* Section 7: Risk */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.risk.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.risk.intro')}</p>
              <div className="space-y-2 text-gray-700 mb-3">
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.risk.items.inherentRisks')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.risk.items.voluntary')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.risk.items.notResponsible')}</p>
                </div>
              </div>
              <p className="text-gray-700">{t('sections.risk.encouragement')}</p>
            </section>

            {/* Section 8: Host Responsibilities */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.hostResponsibilities.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.hostResponsibilities.intro')}</p>
              <div className="space-y-2 text-gray-700 mb-3">
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.hostResponsibilities.items.comply')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.hostResponsibilities.items.professional')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.hostResponsibilities.items.accurate')}</p>
                </div>
              </div>
              <p className="text-gray-700">{t('sections.hostResponsibilities.insuranceNote')}</p>
            </section>

            {/* Section 9: Intellectual Property */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.intellectualProperty.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.intellectualProperty.ownership')}</p>
              <p className="text-gray-700 mb-3">{t('sections.intellectualProperty.userContent')}</p>
              <p className="text-gray-700">{t('sections.intellectualProperty.prohibited')}</p>
            </section>

            {/* Section 10: Disclaimers */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.disclaimers.title')}
              </h2>
              <p className="text-gray-700 mb-3">
                <Trans i18nKey="sections.disclaimers.definition" t={t} components={bold} />
              </p>
              <p className="text-gray-700 mb-3">
                <Trans i18nKey="sections.disclaimers.asIs" t={t} components={bold} />
              </p>
              <p className="text-gray-700 mb-3">{t('sections.disclaimers.warranties')}</p>
              <p className="text-gray-700 mb-3">{t('sections.disclaimers.noGuarantee')}</p>
              <p className="text-gray-700 mb-3">{t('sections.disclaimers.independentHosts')}</p>
              <p className="text-gray-700">{t('sections.disclaimers.assumptionOfRisk')}</p>
            </section>

            {/* Section 11: Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.limitation.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.limitation.intro')}</p>
              <div className="space-y-3 text-gray-700 mb-3 pl-6">
                <p>{t('sections.limitation.items.indirect')}</p>
                <p>
                  <Trans i18nKey="sections.limitation.items.cap" t={t} components={bold} />
                </p>
                <p>{t('sections.limitation.items.thirdParties')}</p>
              </div>
              <p className="text-gray-700 mb-3">{t('sections.limitation.essentialBasis')}</p>
              <p className="text-gray-700">{t('sections.limitation.jurisdictionNote')}</p>
            </section>

            {/* Section 12: Indemnification */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.indemnification.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.indemnification.intro')}</p>
              <div className="space-y-2 text-gray-700 mb-3">
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.indemnification.items.useOfPlatform')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.indemnification.items.participation')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.indemnification.items.content')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p>{t('sections.indemnification.items.violation')}</p>
                </div>
              </div>
              <p className="text-gray-700">{t('sections.indemnification.defense')}</p>
            </section>

            {/* Section 13: Dispute Resolution and Governing Law */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.disputeResolution.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.disputeResolution.law')}</p>
              <p className="text-gray-700 mb-3">{t('sections.disputeResolution.arbitration')}</p>
              <p className="text-gray-700 mb-3">{t('sections.disputeResolution.waiver')}</p>
              <p className="text-gray-700">{t('sections.disputeResolution.equitableRelief')}</p>
            </section>

            {/* Section 14: Contact */}
            <section className="mt-8 p-6 bg-primary-pale rounded-lg">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('sections.contact.title')}
              </h2>
              <p className="text-gray-700 mb-2">{t('sections.contact.intro')}</p>
              <p className="text-gray-700 font-semibold">{t('sections.contact.company')}</p>
              <p className="text-gray-700">{t('sections.contact.address')}</p>
              <p className="text-gray-700">
                <a
                  href={`mailto:${t('sections.contact.email')}`}
                  className="text-primary hover:underline"
                >
                  {t('sections.contact.email')}
                </a>
              </p>
            </section>
          </Card>
        </div>
      </div>
    </>
  );
}
